import React, {useEffect, useState, useCallback, memo} from 'react';
import {
    Grid, TextField, Typography, Button, Box, Alert
} from "@mui/material";
import { useDispatch } from "react-redux";
import { verifyEmail, verifyOtp } from "../index"; // Your API functions
import {useNavigate, Link as LinkUI, useParams} from "react-router-dom";
import { styled } from "@mui/material/styles";

const Link = styled(LinkUI)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '1.2rem',
    '&:hover': {
        cursor: 'pointer',
        color: theme.palette.primary.dark,
    },
}));

export const VerifyEmail = memo(() => {
    const { email: paramEmail } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formState, setFormState] = useState({
        step: 1,
        errors: {},
        message: "",
        status: "info",
        isLoading: false,
        data: { email: "", otp: "", id: "" }
    });

    useEffect(() => {
        setFormState(prev => ({
            ...prev,
            data: { ...prev.data, email: paramEmail || "" }
        }));
    }, [paramEmail]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            data: { ...prev.data, [name]: value },
            errors: { ...prev.errors, [name]: null },
            message: ""
        }));
    }, []);

    const handleEmailSubmit = useCallback(async () => {
        if (!formState.data.email.trim()) {
            setFormState(prev => ({ ...prev, errors: { email: "Email is required." } }));
            return;
        }
        setFormState(prev => ({ ...prev, isLoading: true }));
        const res = await verifyEmail({ dispatch, data: { email: formState.data.email } });

        setFormState(prev => ({ ...prev, isLoading: false }));
        if (res.success) {
            setFormState(prev => ({
                ...prev,
                step: 2,
                data: { ...prev.data, otp: "", id: res.data }
            }));
        }
        setFormState(prev => ({
            ...prev,
            errors: res.errors,
            message: res.message,
            status: res.status
        }));
    }, [formState.data.email, dispatch]);

    const handleOtpSubmit = useCallback(async () => {
        if (!formState.data.otp.trim()) {
            setFormState(prev => ({ ...prev, errors: { otp: "OTP is required." } }));
            return;
        }
        setFormState(prev => ({ ...prev, isLoading: true }));
        const res = await verifyOtp({ dispatch, data: formState.data });

        setFormState(prev => ({ ...prev, isLoading: false }));
        if (res.success) {
            navigate('/register/setup-password');
        }
        setFormState(prev => ({
            ...prev,
            errors: res.errors,
            message: res.message,
            status: res.status
        }));
    }, [formState.data, dispatch, navigate]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            if (formState.step === 1) {
                handleEmailSubmit();
            } else {
                handleOtpSubmit();
            }
        }
    }, [formState.step, handleEmailSubmit, handleOtpSubmit]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography align={"center"} mb={4} fontWeight={"bold"} className={"title"} variant={"h1"}>Verify Email</Typography>
                <Typography align={"center"} mb={4} variant={"body1"}>
                    {formState.step === 1 ? "Enter your email" : "Enter the OTP sent to your email"}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    {formState.message && (
                        <Grid item xs={12}>
                            <Alert severity={formState.status}>{formState.message}</Alert>
                        </Grid>
                    )}

                    {formState.step === 1 && (
                        <Grid item xs={12}>
                            <TextField
                                value={formState.data.email}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                required
                                name="email"
                                label="Email"
                                fullWidth
                                error={!!formState.errors?.email}
                                helperText={formState.errors?.email}
                                disabled={formState.isLoading}
                            />
                        </Grid>
                    )}

                    {formState.step === 2 && (
                        <Grid item xs={12}>
                            <TextField
                                value={formState.data.otp}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                required
                                name="otp"
                                label="OTP"
                                fullWidth
                                error={!!formState.errors?.otp}
                                helperText={formState.errors?.otp}
                                disabled={formState.isLoading}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Button
                            fullWidth={true}
                            variant="contained"
                            disableElevation={true}
                            onClick={formState.step === 1 ? handleEmailSubmit : handleOtpSubmit}
                            disabled={formState.isLoading}
                            size="large"
                            sx={{height: 50, fontSize: 18, textTransform:"capitalize"}}
                        >
                            {formState.step === 1 ? "Send OTP" : "Verify OTP"}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant={"body2"}>
                                Didn't receive the OTP? <Link to="/resend-otp">Resend OTP</Link>
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
});